// One card per PadSplit lock: header (name, scope tag, PadSplit lock id,
// mapping cell), optional stale-instructions warning strip, and the per-room
// code comparison table. Presentational — all mutations live in the parent
// tab (padsplit-sync-tab.tsx). Visuals follow the approved mockup.
//
// One-directional compare by design: extra codes on the physical lock that
// PadSplit doesn't know about are never displayed.
import { useState } from "react";
import { Loader2, AlertTriangle, Check, X, RefreshCw, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  LockCodeComparisonRow,
  LockComparisonGroup,
  PhysicalLockOption,
} from "./padsplit-sync-types";

function StatusBadge({ row }: { row: LockCodeComparisonRow }) {
  switch (row.status) {
    case "in_sync":
      return (
        <Badge className="bg-green-100 text-green-700 border border-green-200" data-testid={`badge-status-in-sync-${row.padsplitCodeId}`}>
          <Check className="h-3 w-3 mr-1" /> In sync
        </Badge>
      );
    case "missing_on_lock":
      return (
        <Badge className="bg-amber-100 text-amber-700 border border-amber-200" data-testid={`badge-status-missing-on-lock-${row.padsplitCodeId}`}>
          <AlertTriangle className="h-3 w-3 mr-1" /> Missing on lock
        </Badge>
      );
    case "pending_push":
      return (
        <Badge className="bg-gray-100 text-gray-600 border border-gray-200" data-testid={`badge-status-pending-push-${row.padsplitCodeId}`}>
          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Pushing…
        </Badge>
      );
    case "pushed_awaiting_sync":
      return (
        <Badge className="bg-purple-100 text-purple-700 border border-purple-200" data-testid={`badge-status-awaiting-sync-${row.padsplitCodeId}`}>
          <RefreshCw className="h-3 w-3 mr-1" /> Pushed — awaiting sync
        </Badge>
      );
    case "push_failed":
      return (
        <Badge className="bg-red-100 text-red-700 border border-red-200" data-testid={`badge-status-push-failed-${row.padsplitCodeId}`}>
          <X className="h-3 w-3 mr-1" /> Push failed
        </Badge>
      );
    case "no_mapping":
      return (
        <Badge className="bg-gray-100 text-gray-500 border border-gray-200" data-testid={`badge-status-no-mapping-${row.padsplitCodeId}`}>
          Map lock to compare
        </Badge>
      );
    default:
      return (
        <Badge className="bg-gray-100 text-gray-500 border border-gray-200" data-testid={`badge-status-unknown-${row.padsplitCodeId}`}>
          Codes unavailable
        </Badge>
      );
  }
}

export interface PadsplitLockCompareCardProps {
  group: LockComparisonGroup;
  physicalLocks: PhysicalLockOption[];
  onConfirmMapping: (group: LockComparisonGroup, deviceId: string) => void;
  mappingPending: boolean;
  onNewCode: (group: LockComparisonGroup, row: LockCodeComparisonRow) => void;
  onAddToLock: (group: LockComparisonGroup, row: LockCodeComparisonRow) => void;
  addToLockPendingCodeId: number | null;
  onRetryPush: (pushId: number) => void;
  retryPendingPushId: number | null;
}

export function PadsplitLockCompareCard({
  group,
  physicalLocks,
  onConfirmMapping,
  mappingPending,
  onNewCode,
  onAddToLock,
  addToLockPendingCodeId,
  onRetryPush,
  retryPendingPushId,
}: PadsplitLockCompareCardProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerDeviceId, setPickerDeviceId] = useState<string>("");

  return (
    <div className="bg-card border rounded-xl overflow-hidden" data-testid={`card-padsplit-lock-${group.padsplitLockKey}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[15px]" data-testid={`text-lock-name-${group.padsplitLockKey}`}>
            {group.lockName ?? "Unnamed lock"}
          </span>
          {group.scope === "common" ? (
            <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-[11px]">Common</Badge>
          ) : group.scope === "room" ? (
            <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[11px]">Room</Badge>
          ) : null}
          <span className="text-xs text-muted-foreground font-mono">PadSplit lock id: {group.padsplitLockKey}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
          {group.mapping ? (
            <>
              <span>Mapped to</span>
              <span className="inline-flex items-center gap-1.5 bg-muted border rounded-md px-2 py-0.5 text-foreground font-medium" data-testid={`text-mapping-${group.padsplitLockKey}`}>
                {group.mapping.displayName ?? group.mapping.deviceId}
                {group.mapping.provider && (
                  <span className="text-muted-foreground font-normal">({group.mapping.provider})</span>
                )}
              </span>
              {group.mapping.batteryPct != null && (
                <span className="text-xs">🔋 {group.mapping.batteryPct}%</span>
              )}
            </>
          ) : (
            <Badge className="bg-gray-100 text-gray-500 border border-gray-200" data-testid={`badge-unmapped-${group.padsplitLockKey}`}>
              Not mapped to a physical lock
            </Badge>
          )}
        </div>
      </div>

      {/* Stale move-in-instructions warning (read-only — we never write instructions) */}
      {group.staleInstructions && (
        <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 text-sm" data-testid={`warn-stale-instructions-${group.padsplitLockKey}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Move-in instructions for this lock mention a code that no longer matches. Update the instructions in PadSplit.
        </div>
      )}

      {/* Mapping suggestion / picker */}
      {!group.mapping && (
        <div className="flex items-center justify-between gap-2 bg-blue-50 border-b border-blue-200 text-blue-900 px-4 py-2 text-sm flex-wrap" data-testid={`strip-mapping-${group.padsplitLockKey}`}>
          {group.suggestion && !showPicker ? (
            <>
              <span className="inline-flex items-center gap-1.5">
                <Lightbulb className="h-4 w-4 shrink-0" />
                Suggested match: <strong>{group.suggestion.displayName ?? group.suggestion.deviceId}</strong>
                <span className="text-blue-700">
                  ({group.suggestion.provider}, {group.suggestion.reason === "room_pin" ? "room pin" : "name match"})
                </span>
              </span>
              <span className="flex gap-2">
                <Button
                  size="sm"
                  disabled={mappingPending}
                  onClick={() => onConfirmMapping(group, group.suggestion!.deviceId)}
                  data-testid={`button-confirm-mapping-${group.padsplitLockKey}`}
                >
                  {mappingPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm mapping"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowPicker(true)} data-testid={`button-choose-different-${group.padsplitLockKey}`}>
                  Choose different lock
                </Button>
              </span>
            </>
          ) : (
            <>
              <span>Choose the physical lock this PadSplit lock controls:</span>
              <span className="flex gap-2 items-center">
                <Select value={pickerDeviceId} onValueChange={setPickerDeviceId}>
                  <SelectTrigger className="w-[240px] h-8 bg-card" data-testid={`select-mapping-${group.padsplitLockKey}`}>
                    <SelectValue placeholder="Select a lock…" />
                  </SelectTrigger>
                  <SelectContent>
                    {physicalLocks.map((lock) => (
                      <SelectItem key={lock.deviceId} value={lock.deviceId}>
                        {(lock.displayName ?? lock.deviceId) + " (" + lock.provider + ")"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  disabled={!pickerDeviceId || mappingPending}
                  onClick={() => onConfirmMapping(group, pickerDeviceId)}
                  data-testid={`button-save-mapping-${group.padsplitLockKey}`}
                >
                  {mappingPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save mapping"}
                </Button>
              </span>
            </>
          )}
        </div>
      )}

      {/* Code rows */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b bg-muted/40">
            <th className="px-4 py-2 font-semibold">Room</th>
            <th className="px-4 py-2 font-semibold">Assigned to</th>
            <th className="px-4 py-2 font-semibold">PadSplit code</th>
            <th className="px-4 py-2 font-semibold">On physical lock</th>
            <th className="px-4 py-2 font-semibold">Status</th>
            <th className="px-4 py-2 font-semibold text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {group.rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-4 text-muted-foreground text-center" data-testid={`text-no-codes-${group.padsplitLockKey}`}>
                No codes found on this lock in PadSplit.
              </td>
            </tr>
          )}
          {group.rows.map((row) => (
            <tr
              key={row.padsplitCodeId}
              className={
                row.status === "missing_on_lock"
                  ? "bg-amber-50/60 border-b last:border-b-0"
                  : row.status === "push_failed"
                    ? "bg-red-50/60 border-b last:border-b-0"
                    : "border-b last:border-b-0"
              }
              data-testid={`row-code-${row.padsplitCodeId}`}
            >
              <td className="px-4 py-2.5">{row.roomLabel ?? "—"}</td>
              <td className="px-4 py-2.5">
                {row.assignedTo ?? <span className="text-muted-foreground">Not occupied</span>}
              </td>
              <td className="px-4 py-2.5 font-mono font-semibold">{row.padsplitCode ?? "—"}</td>
              <td className="px-4 py-2.5 font-mono">
                {row.status === "in_sync" ? (
                  row.physicalCode
                ) : row.status === "missing_on_lock" ? (
                  <span className="text-muted-foreground font-sans">— not found</span>
                ) : row.status === "no_mapping" || row.status === "unknown" ? (
                  <span className="text-muted-foreground font-sans">— unknown</span>
                ) : (
                  <span className="text-muted-foreground font-sans">…</span>
                )}
              </td>
              <td className="px-4 py-2.5">
                <StatusBadge row={row} />
              </td>
              <td className="px-4 py-2.5">
                <div className="flex gap-1.5 justify-end items-center">
                  {row.status === "push_failed" && (
                    <>
                      {row.pushError && (
                        <span className="text-xs text-red-600 max-w-[220px] truncate" title={row.pushError}>
                          {row.pushError}
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200"
                        disabled={retryPendingPushId === row.pushId}
                        onClick={() => row.pushId != null && onRetryPush(row.pushId)}
                        data-testid={`button-retry-push-${row.padsplitCodeId}`}
                      >
                        {retryPendingPushId === row.pushId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Retry"}
                      </Button>
                    </>
                  )}
                  {row.status === "pending_push" && row.pushCreatedAt && (
                    <span className="text-xs text-muted-foreground">queued</span>
                  )}
                  {row.status === "missing_on_lock" && group.mapping && (
                    <Button
                      size="sm"
                      disabled={addToLockPendingCodeId === row.padsplitCodeId}
                      onClick={() => onAddToLock(group, row)}
                      data-testid={`button-add-to-lock-${row.padsplitCodeId}`}
                    >
                      {addToLockPendingCodeId === row.padsplitCodeId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Add to lock"
                      )}
                    </Button>
                  )}
                  {(row.status === "in_sync" || row.status === "missing_on_lock" || row.status === "unknown") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onNewCode(group, row)}
                      data-testid={`button-new-code-${row.padsplitCodeId}`}
                    >
                      New code
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
